import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  Input,
  Box,
  Flex,
  Text,
  Spinner,
  Link,
  List,
  InputGroup,
  Code,
  Kbd,
} from "@chakra-ui/react";
import { LuSearch } from "react-icons/lu";
import { SearchResults } from "./SearchResults";
import type { SearchResult } from "../interfaces";
import { search } from "../api-client";

const SearchTips = () => (
  <Box>
    <Text fontSize="sm" fontWeight="medium" mb={3}>
      Search Tips
    </Text>
    <List.Root fontSize="sm">
      <List.Item>
        Use <Code>policy NOT india</Code> to exclude terms.
      </List.Item>
      <List.Item>
        Use <Code>topics:classical</Code> to search within a specific field.
      </List.Item>
      <List.Item>
        Use <Code>NEAR("india" "china", 5)</Code> for proximity searches.
      </List.Item>
      <List.Item>
        See the full{" "}
        <Link
          colorPalette="cyan"
          href="https://www.sqlite.org/fts5.html#full_text_query_syntax"
        >
          Sqlite FTS syntax guide
        </Link>{" "}
        for more.
      </List.Item>
    </List.Root>
  </Box>
);

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && query.trim() !== "") {
      window.location.href = `/search?q=${encodeURIComponent(query)}`;
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal is closed
      setQuery("");
      setResults([]);
      setLoading(false);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.trim() === "") {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const handler = setTimeout(() => {
      const fetchResults = async () => {
        setError(null);
        try {
          const data = await search(query, "All") as SearchResult[];
          setResults(data);
        } catch (err: any) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchResults();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [query]);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      initialFocusEl={() => initialFocusRef.current}
      size="lg"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxW="2xl" p={0}>
          <Box p={3} borderBottomWidth="1px">
            <InputGroup startElement={<LuSearch />}>
              <Input
                ref={initialFocusRef}
                placeholder="Search recommendations and episodes..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </InputGroup>
          </Box>
          <Dialog.Body p={4} maxH="400px" overflowY="auto">
            {loading ? (
              <Flex justify="center" align="center" h="100px">
                <Spinner size="lg" />
              </Flex>
            ) : error ? (
              <Text color="red.500">{error}</Text>
            ) : results.length > 0 ? (
              <SearchResults
                results={results}
                onItemClick={onClose}
                isModalLayout
              />
            ) : (
              <SearchTips />
            )}
          </Dialog.Body>
          <Dialog.Footer justifyContent="start" borderTopWidth="1px" p={2}>
            <Text fontSize="sm" color="gray.500">
              <Kbd>Esc</Kbd> to close
            </Text>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
